import { CanvasTexture, LinearFilter, RepeatWrapping, SRGBColorSpace } from 'three';

export interface NumberTextureSpec {
  digit: number;
  /** Hex color string ("#rrggbb") for ball body. */
  color: string;
  /** "standard" = colored body + white ovals + black digit. "sicbo" = white body + black digit (no oval). */
  style: 'standard' | 'sicbo';
  /** How many digit cells around the equator (default 6). */
  repeats?: number;
  /** Cell width in px (texture width = repeats * cellWidth). Default 256. */
  cellWidth?: number;
  /** Cell height in px (== texture height). Default 512. */
  cellHeight?: number;
  /** Optional override for digit color. */
  digitColor?: string;
}

/**
 * Produce a CanvasTexture whose UV layout repeats the digit `repeats` times around
 * the equator of a SphereGeometry. Each cell draws an optional white oval backdrop
 * (standard style) and the digit centered. The result is readable from any angle
 * as the ball tumbles.
 */
export function makeNumberTexture(spec: NumberTextureSpec): CanvasTexture {
  const repeats = spec.repeats ?? 6;
  const cellW = spec.cellWidth ?? 256;
  const cellH = spec.cellHeight ?? 512;
  const width = repeats * cellW;
  const height = cellH;

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2D context for NumberTexture');

  // Background — solid color (or white for sicbo).
  ctx.fillStyle = spec.style === 'sicbo' ? '#f4f4f4' : spec.color;
  ctx.fillRect(0, 0, width, height);

  // Optional subtle gradient sheen down each cell to give the sphere depth.
  if (spec.style === 'standard') {
    for (let i = 0; i < repeats; i++) {
      const x0 = i * cellW;
      const grad = ctx.createLinearGradient(x0, 0, x0, height);
      grad.addColorStop(0, 'rgba(255,255,255,0.18)');
      grad.addColorStop(0.5, 'rgba(255,255,255,0)');
      grad.addColorStop(1, 'rgba(0,0,0,0.12)');
      ctx.fillStyle = grad;
      ctx.fillRect(x0, 0, cellW, height);
    }
  }

  const ovalRadiusX = cellW * 0.36;
  const ovalRadiusY = cellH * 0.32;
  const digitColor = spec.digitColor ?? '#101010';
  const fontPx = Math.floor(cellH * 0.46);
  const digitText = String(spec.digit);

  for (let i = 0; i < repeats; i++) {
    const cx = i * cellW + cellW / 2;
    const cy = height / 2;

    if (spec.style === 'standard') {
      // White oval backdrop with a soft outline.
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.ellipse(cx, cy, ovalRadiusX, ovalRadiusY, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.12)';
      ctx.lineWidth = 4;
      ctx.stroke();
    }

    // Digit (bold sans-serif).
    ctx.fillStyle = digitColor;
    ctx.font = `900 ${fontPx}px "Helvetica Neue", Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(digitText, cx, cy);
  }

  const tex = new CanvasTexture(canvas);
  tex.wrapS = RepeatWrapping;
  tex.wrapT = RepeatWrapping;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.colorSpace = SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}
